// src/stores/map/mergeDisplaySettings.ts
//
// Pure merge utility for restoring a project View's saved display settings
// onto the current map store state (EVA-63).
//
// Rule: this is a PER-CATEGORY merge, never a wholesale replace. Only the
// categories present in `saved` (and that pass their type guard) overwrite
// `current`; every other category — including ones a stale/partial View
// doesn't know about — is left untouched. This lets a partial or corrupted
// saved object restore what it can without ever wiping the rest of the map.
import * as Sentry from "@sentry/react";

import type { MapDisplaySettings } from "./mapTypes";

/**
 * A finite number — rejects `NaN`/`±Infinity`, which `typeof` alone would let
 * through (`typeof NaN === "number"`) and which could otherwise reach
 * unguarded Mapbox paint/interpolation expressions from a hand-trimmed or
 * corrupted saved View.
 */
function isValidScalar(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * A non-null, non-array object — required for the 3 record categories.
 * Note: this validates the record's SHAPE only, not the type of its nested
 * values (e.g. it won't catch `{ wells: "not-a-number" }` inside
 * `labelDensity`). That's a documented residual — consumers of these records
 * (the layer filter/zoom/density features) already guard their own value
 * reads, so a malformed nested value degrades gracefully rather than reaching
 * an unguarded Mapbox call.
 */
function isValidRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A saved category was PRESENT but failed its guard (malformed — distinct
 * from simply being absent from `saved`). Surfaced as a breadcrumb, not a
 * throw, so a corrupted View is visible in monitoring rather than silently
 * swallowed.
 */
function noteRejectedCategory(key: string, value: unknown): void {
  Sentry.addBreadcrumb({
    message: `mergeDisplaySettings: rejected malformed "${key}" category`,
    category: "map_display_settings",
    level: "warning",
    data: { key, valueType: typeof value }
  });
}

/**
 * Merges a View's saved display settings onto `current`, one category at a
 * time (FR-007/FR-009/FR-012/FR-013/FR-014). Never throws.
 *
 * FORWARD-COMPATIBLE FOR REQUIRED CATEGORIES (FR-017): there is NO hardcoded
 * list of categories. We iterate the keys the live store `current` actually
 * has, and for each one present in `saved` we apply it if its saved value
 * matches the runtime SHAPE of the current value (a finite number for a scalar
 * setting, or a plain object for a per-layer record). So any REQUIRED display
 * setting added to the store round-trips automatically — no edit to this file.
 *
 * CAVEAT — optional categories must be pre-seeded. The guard is derived from
 * the runtime shape of `current[key]`, so a category whose current value is
 * `undefined` (an OPTIONAL setting the live state hasn't populated — e.g. after
 * a wholesale setDisplaySettings replace that carries no defaults) matches
 * neither branch and its saved value is silently skipped, with no breadcrumb.
 * A new optional display setting therefore does NOT round-trip for free: seed
 * it into `current` at a concrete default before merge, exactly as
 * `applyViewDisplaySettings` seeds `layerFilterSettings` via
 * RIG_FRAC_DISPLAY_DEFAULTS. Required categories (always present at a real
 * shape) are unaffected.
 *
 * - `saved` absent/null/non-object → returns `current` BY REFERENCE, so
 *   callers (the store action) can use identity to detect a true no-op and
 *   skip notifying subscribers.
 * - A category present in `saved` whose value doesn't match the current
 *   shape (malformed) is skipped (breadcrumb, no throw); a key in `saved`
 *   that the store no longer recognizes is ignored.
 */
export function mergeDisplaySettings<T extends MapDisplaySettings>(
  current: T,
  saved?: Partial<MapDisplaySettings> | null
): T {
  if (!saved || typeof saved !== "object") {
    return current;
  }

  let changed = false;
  const updates: GenericObject = {};
  const savedRecord = saved as GenericObject;

  for (const key of Object.keys(current)) {
    if (!(key in savedRecord)) continue;
    const currentValue = (current as GenericObject)[key];
    const savedValue = savedRecord[key];

    if (typeof currentValue === "number") {
      // Scalar setting (thickness, label/icon size, …): require a finite number.
      if (isValidScalar(savedValue)) {
        updates[key] = savedValue;
        changed = true;
      } else {
        noteRejectedCategory(key, savedValue);
      }
    } else if (isValidRecord(currentValue)) {
      // Per-layer record (zoom/density/filter, …): replaces its whole category.
      if (isValidRecord(savedValue)) {
        updates[key] = savedValue;
        changed = true;
      } else {
        noteRejectedCategory(key, savedValue);
      }
    }
    // Any other current shape is not a recognized display-setting category → skip.
  }

  return changed ? { ...current, ...updates } : current;
}
